import { useRef, useEffect, useState } from "react";
import { useSurveyStore } from "@src/store/survey";
import { Select, Flex, Text, Box } from "@radix-ui/themes";
import { useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { recordResponse } from "@api/survey";
import PropTypes from "prop-types";
import { Trans } from "@lingui/macro";

SelectQuestion.propTypes = {
  question: PropTypes.object,
};

export default function SelectQuestion({ question }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState();

  const sessionUUID = useSurveyStore((state) => state.sessionUUID);
  const surveyResponses = useSurveyStore((state) => state.surveyResponses);
  const setSurveyResponses = useSurveyStore(
    (state) => state.setSurveyResponses,
  );
  const mapRef = useSurveyStore((state) => state.getMapRef());
  const uuid = useRef(null);
  const response = useMutation({
    mutationFn: recordResponse,
    onSuccess: (data) => {
      setSurveyResponses((state) => {
        return { ...state.surveyResponses, [question.uuid]: data };
      });
      let index = question.options.options.findIndex(
        (option) => option.label === data.response.selected.label,
      );
      setSelected(index);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  function truncateString(str, num) {
    if (str.length <= num) {
      return str;
    } else {
      return str.slice(0, num) + "...";
    }
  }

  useEffect(() => {
    uuid.current = self.crypto.randomUUID();
  }, []);

  useEffect(() => {
    if (
      question !== undefined &&
      surveyResponses[question.uuid] !== undefined
    ) {
      let value = surveyResponses[question.uuid].response.selected.label;
      let index = question.options.options.findIndex(
        (option) => option.label === value,
      );
      setSelected(index);
    }
  }, [surveyResponses, question]);

  function hasAllKeys(obj, keys) {
    return keys.every((key) => key in obj);
  }

  async function valueChange(value) {
    if (value === -1) {
      return;
    }

    let selected = question.options.options[value];

    if (hasAllKeys(selected, ["latitude", "longitude", "default_zoom"])) {
      let currentParams = new URLSearchParams(searchParams.toString());

      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${currentParams.toString()}`,
      );

      mapRef?.flyTo({
        center: [selected.longitude, selected.latitude],
        zoom: selected.default_zoom,
      });
    }

    let responseData = {
      selected: selected,
    };

    response.mutate({
      answerUUID: uuid.current,
      response: responseData,
      sessionUUID: sessionUUID,
      surveyQuestionUUID: question.uuid,
    });
  }

  if (question !== undefined) {
    return (
      <Flex direction="column">
        <Text as="p" size="3" mb="2" weight="bold">
          {question.question}
        </Text>
        <Text as="p" size="1" color="gray" mb="2">
          {question.hint_text}
        </Text>
        <Select.Root
          defaultValue={-1}
          onValueChange={valueChange}
          value={selected}
        >
          <Select.Trigger />
          <Box maxWidth="calc(100% - 1rem)" asChild>
            <Select.Content>
              <Select.Group>
                <Select.Item value={-1} key={-1}>
                  <Text as="span" size="1" color="gray">
                    {question.options.default !== undefined ? (
                      question.options.default
                    ) : (
                      <Trans id="question.select_option">
                        Select an option
                      </Trans>
                    )}
                  </Text>
                </Select.Item>
                {question.options.options.map((option, idx) => (
                  <Select.Item value={idx} key={idx}>
                    <Text as="span" size="2">
                      {truncateString(option.label, 36)}
                    </Text>
                  </Select.Item>
                ))}
              </Select.Group>
            </Select.Content>
          </Box>
        </Select.Root>
      </Flex>
    );
  } else {
    return null;
  }
}
